import React, { useState, useMemo, useEffect } from "react";
import Modal, { ModalActions } from "./Modal";

/**
 * Reusable formula editor modal — same chip palette and syntax help as the
 * Settings → Variable Templates form. Keeps the formula authoring experience
 * consistent across Variable Templates, Input Data assumptions, and Budget
 * Summary cells.
 *
 * Two authoring modes:
 *   "single"  — one expression that applies to every year. Use the
 *               IS_ACTUAL / IS_BUDGETED / IS_PROJECTED placeholders +
 *               IF(cond, a, b) if year-specific logic is needed.
 *   "split"   — three separate expressions (Actual / Budgeted / Projected)
 *               that get compiled to an IS_* IF-chain on save and parsed
 *               back from the same pattern on next open. Storage stays a
 *               single string — no schema change.
 *
 * Props:
 *   title            — modal heading (e.g. "Formula — Occupancy Growth %")
 *   initialFormula   — string, the current formula expression (or "")
 *   variableNames    — array of strings to surface as "Reference another variable" chips
 *   selfName         — name of the variable being edited (used in @PREV self-ref hint)
 *   onCancel         — () => void
 *   onSave           — (newFormula: string) => Promise|void  (called with empty string to clear)
 */

// Match the IS_* IF-chain pattern produced by buildCombined() so re-opening
// a previously-split formula populates the three inputs instead of dumping
// the raw expression into Single mode. Tolerates extra whitespace around
// operators, parens, and commas.
const SPLIT_PATTERN = /^\s*IF\s*\(\s*IS_ACTUAL\s*,(.+?),\s*IF\s*\(\s*IS_BUDGETED\s*,(.+?),(.+?)\)\s*\)\s*$/s;

function parseSplit(expr) {
  if (!expr) return null;
  // Try to match — but only if the parens balance cleanly so we don't
  // mis-parse a nested formula whose inner expression contains commas.
  const m = expr.match(SPLIT_PATTERN);
  if (!m) return null;
  // The regex is greedy on the third capture; balance-check by counting
  // parens in each piece — if any is unbalanced, fall back to single mode.
  const balanced = (s) => {
    let d = 0;
    for (const c of s) { if (c === "(") d++; else if (c === ")") d--; if (d < 0) return false; }
    return d === 0;
  };
  const [, a, b, c] = m;
  if (!balanced(a) || !balanced(b) || !balanced(c)) return null;
  return { actual: a.trim(), budgeted: b.trim(), projected: c.trim() };
}

function buildCombined(actual, budgeted, projected) {
  const a = (actual || "").trim() || "0";
  const b = (budgeted || "").trim() || "0";
  const p = (projected || "").trim() || "0";
  // If all three collapse to "0" treat as "clear formula".
  if (a === "0" && b === "0" && p === "0") return "";
  return `IF(IS_ACTUAL, ${a}, IF(IS_BUDGETED, ${b}, ${p}))`;
}

export default function FormulaModal({
  title, initialFormula = "", variableNames = [], selfName = null,
  onCancel, onSave,
}) {
  const initSplit = useMemo(() => parseSplit(initialFormula || ""), [initialFormula]);
  // "split" if the saved formula matches the IS_* IF chain we produce;
  // "single" otherwise (raw expression authoring, the original behavior).
  const [mode, setMode] = useState(initSplit ? "split" : "single");
  const [formula, setFormula] = useState(initialFormula || "");
  const [splitActual, setSplitActual] = useState(initSplit?.actual || "");
  const [splitBudgeted, setSplitBudgeted] = useState(initSplit?.budgeted || "");
  const [splitProjected, setSplitProjected] = useState(initSplit?.projected || "");
  // Which split-mode input is the "active" target for chip insertion. Lets
  // the user click chips and have tokens appended into the currently-focused
  // Actual/Budgeted/Projected box rather than always the first one.
  const [splitFocus, setSplitFocus] = useState("actual");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Keep the Single-mode textbox in sync with the Split inputs so toggling
  // back to Single shows the compiled equivalent (and saving from Single
  // doesn't lose changes made in Split).
  useEffect(() => {
    if (mode === "split") {
      setFormula(buildCombined(splitActual, splitBudgeted, splitProjected));
    }
  }, [mode, splitActual, splitBudgeted, splitProjected]);

  const append = (token, padBefore = false) => {
    const inject = (prev) => {
      const needsSpace = padBefore && prev && !/[\s+\-*/(]$/.test(prev);
      return prev + (needsSpace ? " " : "") + token;
    };
    if (mode === "single") {
      setFormula(inject);
      return;
    }
    // Split mode — route into the focused input.
    if (splitFocus === "budgeted") setSplitBudgeted(inject);
    else if (splitFocus === "projected") setSplitProjected(inject);
    else setSplitActual(inject);
  };

  const sortedVarNames = useMemo(
    () => Array.from(new Set(variableNames || [])).sort(),
    [variableNames]
  );
  const q = (search || "").trim().toLowerCase();
  const filteredNames = q
    ? sortedVarNames.filter((n) => n.toLowerCase().includes(q))
    : sortedVarNames;

  const handleSave = async () => {
    setSaving(true);
    try {
      const out = mode === "split"
        ? buildCombined(splitActual, splitBudgeted, splitProjected)
        : formula;
      await onSave(out);
    } finally { setSaving(false); }
  };

  // Highlight the focused split-mode input so chip clicks are obviously
  // targeting the right box.
  const splitInputStyle = (which) => ({
    borderColor: splitFocus === which ? "var(--accent)" : undefined,
    boxShadow: splitFocus === which ? "0 0 0 2px rgba(124,143,245,0.18)" : undefined,
  });

  const previewCombined = mode === "split"
    ? buildCombined(splitActual, splitBudgeted, splitProjected)
    : "";

  return (
    <Modal title={title} width="800px" onClose={onCancel}>
      {/* ── Authoring mode switch: Single expression vs separate Actual /
          Budgeted / Projected boxes. Both produce a single stored string;
          Split compiles to `IF(IS_ACTUAL, A, IF(IS_BUDGETED, B, P))`. */}
      <div className="tabs mb-12" style={{ padding: 3 }}>
        <button type="button" className={`tab ${mode === "single" ? "active" : ""}`}
          onClick={() => setMode("single")}>
          Single Formula
        </button>
        <button type="button" className={`tab ${mode === "split" ? "active" : ""}`}
          onClick={() => setMode("split")}
          title="Author separate expressions for Actual / Budgeted / Projected years.">
          Per Year Kind
        </button>
      </div>

      <div style={{ background: "rgba(124,143,245,0.05)", border: "1px solid rgba(124,143,245,0.18)", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
        <p className="text-xxs fw-600 mb-6" style={{ color: "var(--accent)" }}>
          Click to insert
          {mode === "split" && (
            <span className="text-xxs text-dim" style={{ marginLeft: 8, fontWeight: 400 }}>
              → inserts into the <strong style={{ color: "var(--accent)" }}>{splitFocus}</strong> box
            </span>
          )}
        </p>

        <div className="flex gap-6 mb-8" style={{ flexWrap: "wrap" }}>
          {[
            ["ACTUAL", "Actual year value"],
            ["BUDGETED", "Budgeted year value"],
            ["YEAR_INDEX", "1, 2, 3..."],
            ["GROWTH_RATE", "(BUD-ACT)/ACT"],
            ["PREV", "Previous year of THIS variable"],
            ["YEAR", "FY-end year (2025, 2026...)"],
            ["DAYS_IN_FY", "365 / 366 (leap-year aware)"],
          ].map(([n, d]) => (
            <button key={n} type="button" title={d}
              onClick={() => append(n, true)}
              style={{
                cursor: "pointer", border: "1px solid rgba(124,143,245,0.35)",
                background: "rgba(124,143,245,0.12)", color: "var(--accent)",
                padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11.5, fontWeight: 500,
              }}>{n}</button>
          ))}
          {mode === "single" && (
            <>
              <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
              {[
                ["IS_ACTUAL",    "true (1) only on the Actual year"],
                ["IS_BUDGETED",  "true (1) only on the Budgeted year"],
                ["IS_PROJECTED", "true (1) on Projected years (year ≥ Actual+2)"],
              ].map(([n, d]) => (
                <button key={n} type="button" title={d}
                  onClick={() => append(n, true)}
                  style={{
                    cursor: "pointer", border: "1px solid rgba(245,158,11,0.35)",
                    background: "rgba(245,158,11,0.10)", color: "var(--orange-light)",
                    padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 11.5, fontWeight: 500,
                  }}>{n}</button>
              ))}
              <button type="button"
                title="IF(condition, valueIfTrue, valueIfFalse)"
                onClick={() => append("IF(", true)}
                style={{
                  cursor: "pointer", border: "1px solid rgba(245,158,11,0.35)",
                  background: "rgba(245,158,11,0.10)", color: "var(--orange-light)",
                  padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11.5, fontWeight: 500,
                }}>IF(</button>
            </>
          )}
          <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
          {["+", "-", "*", "/", "(", ")", ","].map((op) => (
            <button key={op} type="button"
              onClick={() => append(" " + op + " ")}
              style={{
                cursor: "pointer", border: "1px solid var(--border-input)",
                background: "rgba(255,255,255,0.05)", color: "var(--text-primary)",
                padding: "3px 9px", borderRadius: 6, fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12, minWidth: 28,
              }}>{op}</button>
          ))}
        </div>

        {sortedVarNames.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-6 mt-8" style={{ gap: 8, flexWrap: "wrap" }}>
              <p className="text-xxs fw-600" style={{ color: "var(--green-light)" }}>
                Reference another variable
                <span className="text-xxs text-dim" style={{ marginLeft: 6, fontWeight: 400 }}>
                  ({filteredNames.length}{q ? ` of ${sortedVarNames.length}` : ""})
                </span>
              </p>
              <input className="input input-sm" type="text"
                placeholder="Search variables…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 200, textAlign: "left" }} />
            </div>
            {filteredNames.length === 0 && (
              <p className="text-xxs text-dim" style={{ padding: "4px 0" }}>
                No variables match "{search}".
              </p>
            )}
            <div className="flex gap-4" style={{ flexWrap: "wrap", maxHeight: 110, overflowY: "auto" }}>
              {filteredNames.map((n) => (
                <span key={n} className="flex items-center" style={{ gap: 0 }}>
                  <button type="button" title={`Insert {${n}} — same-year value`}
                    onClick={() => append(`{${n}}`, true)}
                    style={{
                      cursor: "pointer",
                      border: "1px solid rgba(52,211,153,0.3)",
                      borderRight: "none",
                      borderRadius: "6px 0 0 6px",
                      background: "rgba(52,211,153,0.08)", color: "var(--green-light)",
                      padding: "2px 8px", fontSize: 11,
                    }}>{n}</button>
                  <button type="button" title={`Insert {${n}@PREV} — previous-year value`}
                    onClick={() => append(`{${n}@PREV}`, true)}
                    style={{
                      cursor: "pointer",
                      border: "1px solid rgba(52,211,153,0.3)",
                      borderRadius: "0 6px 6px 0",
                      background: "rgba(52,211,153,0.18)", color: "var(--green-light)",
                      padding: "2px 6px", fontSize: 10, fontWeight: 600,
                    }}>@PREV</button>
                </span>
              ))}
            </div>
            <p className="text-xxs text-dim mt-6">
              <code className="mono">{"{Var Name}"}</code> = same-year value &nbsp;·&nbsp;
              <code className="mono">{"{Var Name@PREV}"}</code> = previous-year value of that variable.
              {selfName && <> &nbsp;·&nbsp; <code className="mono">{`{${selfName}@PREV}`}</code> works for self-reference.</>}
            </p>
          </>
        )}
      </div>

      {mode === "single" && (
        <div className="form-group">
          <label className="form-label">Formula Expression</label>
          <input className="input mono" value={formula}
            onChange={(e) => setFormula(e.target.value)}
            placeholder="e.g. IF(IS_PROJECTED, PREV * (1 + {Occupancy Growth %}), {Occupancy Growth %})" />
          <p className="text-xxs text-dim mt-4">
            Leave blank to clear. Use <code className="text-accent">IS_ACTUAL</code> / <code className="text-accent">IS_BUDGETED</code> / <code className="text-accent">IS_PROJECTED</code> with <code className="text-accent">IF(cond, a, b)</code> for mixed actual/projected logic — or switch to <strong>Per Year Kind</strong> above for separate boxes.
          </p>
        </div>
      )}

      {mode === "split" && (
        <div className="form-group">
          <label className="form-label">Per Year Kind</label>
          <p className="text-xxs text-muted mb-8">
            Author a different expression for each year kind. Leave a box blank
            to fall back to <code className="mono">0</code> for that year.
            Internally these get combined into a single
            <code className="mono"> IF(IS_ACTUAL, …)</code> expression — no schema change.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px 12px", alignItems: "center" }}>
            <span className="text-xs fw-600" style={{ color: "var(--blue)" }}>Actual</span>
            <input className="input mono" value={splitActual}
              onFocus={() => setSplitFocus("actual")}
              onChange={(e) => setSplitActual(e.target.value)}
              placeholder="e.g. ACTUAL  or  {Rooms Sold} / {Total Room Count}"
              style={splitInputStyle("actual")} />

            <span className="text-xs fw-600" style={{ color: "var(--blue)" }}>Budgeted</span>
            <input className="input mono" value={splitBudgeted}
              onFocus={() => setSplitFocus("budgeted")}
              onChange={(e) => setSplitBudgeted(e.target.value)}
              placeholder="e.g. BUDGETED  or  {Rooms Sold} / {Total Room Count}"
              style={splitInputStyle("budgeted")} />

            <span className="text-xs fw-600" style={{ color: "var(--accent)" }}>Projected</span>
            <input className="input mono" value={splitProjected}
              onFocus={() => setSplitFocus("projected")}
              onChange={(e) => setSplitProjected(e.target.value)}
              placeholder="e.g. PREV * (1 + {Occupancy Growth %})"
              style={splitInputStyle("projected")} />
          </div>

          <p className="text-xxs text-dim mt-8">
            <span className="fw-600" style={{ color: "var(--text-secondary)" }}>Compiled:</span>
            &nbsp;<code className="mono" style={{ wordBreak: "break-all" }}>
              {previewCombined || "(empty — saving will clear the formula)"}
            </code>
          </p>
        </div>
      )}

      <ModalActions
        onCancel={onCancel}
        onConfirm={handleSave}
        confirmLabel={saving ? "Saving..." : ((mode === "split" ? previewCombined : formula) ? "Save Formula" : "Clear Formula")}
        disabled={saving} />
    </Modal>
  );
}
